import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import Typography from '@lib/components/Typography/Typography';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import CountMark, {
  CountMarkSizes,
  CountMarkTypes,
} from '@lib/components/CountMark/CountMark';
import getAiToolLabel from './utils/getAiToolLabel';
import groupToolStepsByCategory from './utils/groupToolStepsByCategory';
import useDelayedRunning from './hooks/useDelayedRunning';
import styles from './AiAssistant.module.scss';

export type AiToolStep = {
  id: string;
  toolName: string;
  // True while the tool is still running (spinner); a completed step shows a
  // check. Persisted steps omit it — they are always done.
  running?: boolean;
};

// One line of the strip. The thinking state is a step row too — it is the same
// in-progress shape as a running tool, just with a generic label — so both go
// through here and stay aligned on one icon column.
function StepRow({
  running,
  label,
  count,
  // Off for the standalone "thinking" row (always running, never part of a
  // merged count) so it keeps showing its spinner immediately instead of
  // waiting out the delay meant to smooth a burst of quick tool calls.
  delayIndicator = true,
}: {
  running?: boolean;
  label: string;
  count?: number;
  delayIndicator?: boolean;
}) {
  const displayRunning = useDelayedRunning(running, delayIndicator);
  return (
    <div className={styles.toolActivity}>
      <MaterialIcon
        icon={displayRunning ? 'progress_activity' : 'check_circle'}
        symbolsOutlined={displayRunning}
        size="md-20"
        className={
          displayRunning ? styles.toolActivitySpinner : styles.toolActivityIcon
        }
      />
      <Typography variant="caption">{label}</Typography>
      {!!count && count > 1 && (
        <CountMark
          size={CountMarkSizes.small}
          type={CountMarkTypes.grayLite}
          value={count}
        />
      )}
    </div>
  );
}

type Props = {
  toolSteps: AiToolStep[];
  showThinking?: boolean;
  // Adds a bottom divider separating the steps from the answer markdown.
  // Omitted when the strip stands alone (a tool-only turn with no answer).
  withDivider?: boolean;
};

// The completed tool invocations of an assistant turn, folded into the top of
// the answer bubble as a compact, de-emphasised header so a multi-tool turn
// reads as a single grouped message under one avatar (see ConversationPane
// grouping) instead of a stack of duplicated avatar rows.
function ToolStepsStrip({
  toolSteps,
  showThinking = false,
  withDivider = true,
}: Props) {
  const { t } = useTranslation();
  if (!toolSteps.length && !showThinking) return null;
  const groups = groupToolStepsByCategory(toolSteps);
  return (
    <div
      className={classNames(styles.toolSteps, {
        [styles.toolStepsDivider]: withDivider,
      })}
    >
      {groups.map((group) => (
        <StepRow
          key={group.key}
          running={group.step.running}
          label={getAiToolLabel(group.step.toolName, t)}
          count={group.count}
        />
      ))}
      {showThinking && (
        <StepRow running delayIndicator={false} label={t('ai-thinking')} />
      )}
    </div>
  );
}

export default ToolStepsStrip;
